import { SelectGroup } from 'components/AGSelect/AGGroupedSelect/AGGroupedSelect';
import { SelectOption } from 'components/AGSelect/AGSimpleSelect/AGSimpleSelect';
import {
    DriverDestination,
    DriverDestinationCategory,
    DriverTrends,
    PeakRentalByDayOfWeek,
    PeakRentalByHour,
    InsightsPeriod,
    TenantInsightsPeriod,
    TrendRentalType,
    trendRentalType,
    TenantUsageMonthly,
    DriverReviewRegionalRanking,
} from 'types/PropertyInsights';
import { SpotInterface } from 'types/SpotInterface';
import { getDayFnToDayName } from 'utils/constants';
import { getMonthYearFromString } from 'utils/date';

export const insightsPeriodLabels: Record<InsightsPeriod, string> = {
    [InsightsPeriod.THIRTY_DAYS]: 'Last 30 Days',
    [InsightsPeriod.NINETY_DAYS]: 'Last 90 Days',
    [InsightsPeriod.SIX_MONTHS]: 'Last 6 Months',
    [InsightsPeriod.TWELVE_MONTHS]: 'Last 12 Months',
    [InsightsPeriod.ALL_TIME]: 'All Time',
};

export const insightsPeriodOptions: SelectOption[] = Object.keys(insightsPeriodLabels).map((key) => ({
    value: key,
    label: insightsPeriodLabels[key as InsightsPeriod],
}));

export const tenantInsightsPeriodLabels: Record<TenantInsightsPeriod, string> = {
    [TenantInsightsPeriod.THREE_MONTHS]: 'Last 3 Months',
    [TenantInsightsPeriod.SIX_MONTHS]: 'Last 6 Months',
    [TenantInsightsPeriod.TWELVE_MONTHS]: 'Last 12 Months',
    [TenantInsightsPeriod.TWENTY_FOUR_MONTHS]: 'Last 24 Months',
};

export const tenantInsightsPeriodOptions: SelectOption[] = Object.keys(tenantInsightsPeriodLabels).map((key) => ({
    value: key,
    label: tenantInsightsPeriodLabels[key as TenantInsightsPeriod],
}));

export function formatCount(n: number | null): string {
    return (n ?? 0).toLocaleString('en-US');
}

//-------------------- Driver Trends --------------------//
export enum TrendPeriod {
    THREE = '3',
    SIX = '6',
    TWELVE = '12',
    TWENTY_FOUR = '24',
}

export const trendPeriodOptions: SelectOption[] = [
    {
        value: TrendPeriod.THREE,
        label: 'Last 3 Months',
    },
    {
        value: TrendPeriod.SIX,
        label: 'Last 6 Months',
    },
    {
        value: TrendPeriod.TWELVE,
        label: 'Last 12 Months',
    },
    {
        value: TrendPeriod.TWENTY_FOUR,
        label: 'Last 24 Months',
    },
];

const trendTypesConfig: Record<TrendRentalType, { label: string; color: string }> = {
    [trendRentalType.HOURLY]: { label: 'Hourly', color: '#FF6712' },
    [trendRentalType.DAILY]: { label: 'Daily', color: '#FFC9AF' },
    [trendRentalType.FLAT_RATE]: { label: 'Flat Rate', color: '#FFA581' },
    [trendRentalType.MONTHLY]: { label: 'Individuals', color: '#009AD8' },
    [trendRentalType.TENANT]: { label: 'Tenants', color: '#8AC9EF' },
    [trendRentalType.ENTERPRISE]: { label: 'Enterprise', color: '#BD0C78' },
};

export function getDataForDriverTrendsGraph(report: DriverTrends) {
    const hourlyTrend: number[] = [];
    const dailyTrend: number[] = [];
    const individualTrend: number[] = [];
    const tenantTrend: number[] = [];
    const flatTrend: number[] = [];
    const enterpriseTrend: number[] = [];

    const labels: string[] = [];

    for (const {
        report_month,
        hourly_rentals,
        daily_rentals,
        monthly_rentals,
        tenant_rentals,
        enterprise_rentals,
        flat_rate_rentals,
    } of report.trends) {
        labels.push(getMonthYearFromString(report_month));
        hourlyTrend.push(hourly_rentals);
        dailyTrend.push(daily_rentals);
        individualTrend.push(monthly_rentals);
        tenantTrend.push(tenant_rentals);
        flatTrend.push(flat_rate_rentals);
        if (report.avg_enterprise_rentals > 0) enterpriseTrend.push(enterprise_rentals);
    }

    return {
        labels,
        [trendRentalType.HOURLY]: {
            data: hourlyTrend,
            borderColor: trendTypesConfig[trendRentalType.HOURLY].color,
            backgroundColor: trendTypesConfig[trendRentalType.HOURLY].color,
            label: trendTypesConfig[trendRentalType.HOURLY].label,
        },
        [trendRentalType.FLAT_RATE]: {
            data: flatTrend,
            borderColor: trendTypesConfig[trendRentalType.FLAT_RATE].color,
            backgroundColor: trendTypesConfig[trendRentalType.FLAT_RATE].color,
            label: trendTypesConfig[trendRentalType.FLAT_RATE].label,
        },
        [trendRentalType.DAILY]: {
            data: dailyTrend,
            borderColor: trendTypesConfig[trendRentalType.DAILY].color,
            backgroundColor: trendTypesConfig[trendRentalType.DAILY].color,
            label: trendTypesConfig[trendRentalType.DAILY].label,
        },
        [trendRentalType.MONTHLY]: {
            data: individualTrend,
            borderColor: trendTypesConfig[trendRentalType.MONTHLY].color,
            backgroundColor: trendTypesConfig[trendRentalType.MONTHLY].color,
            label: trendTypesConfig[trendRentalType.MONTHLY].label,
        },
        [trendRentalType.TENANT]: {
            data: tenantTrend,
            borderColor: trendTypesConfig[trendRentalType.TENANT].color,
            backgroundColor: trendTypesConfig[trendRentalType.TENANT].color,
            label: trendTypesConfig[trendRentalType.TENANT].label,
        },

        [trendRentalType.ENTERPRISE]: {
            data: enterpriseTrend,
            borderColor: trendTypesConfig[trendRentalType.ENTERPRISE].color,
            backgroundColor: trendTypesConfig[trendRentalType.ENTERPRISE].color,
            label: trendTypesConfig[trendRentalType.ENTERPRISE].label,
        },
    };
}

export function buildGroups(report: DriverTrends): SelectGroup[] {
    const {
        avg_hourly_rentals,
        avg_flat_rate_rentals,
        avg_daily_rentals,
        avg_monthly_rentals,
        avg_tenant_rentals,
        avg_enterprise_rentals,
    } = report;
    const groups: SelectGroup[] = [
        {
            groupLabel: 'Transient',
            items: [
                {
                    value: trendRentalType.HOURLY,
                    label: trendTypesConfig[trendRentalType.HOURLY].label,
                    color: trendTypesConfig[trendRentalType.HOURLY].color,
                    subLabel: String(formatCount(avg_hourly_rentals)),
                },
                {
                    value: trendRentalType.FLAT_RATE,
                    label: trendTypesConfig[trendRentalType.FLAT_RATE].label,
                    color: trendTypesConfig[trendRentalType.FLAT_RATE].color,
                    subLabel: String(formatCount(avg_flat_rate_rentals)),
                },
                {
                    value: trendRentalType.DAILY,
                    label: trendTypesConfig[trendRentalType.DAILY].label,
                    color: trendTypesConfig[trendRentalType.DAILY].color,
                    subLabel: String(formatCount(avg_daily_rentals)),
                },
            ],
        },
        {
            groupLabel: 'Monthly',
            items: [
                {
                    value: trendRentalType.MONTHLY,
                    label: trendTypesConfig[trendRentalType.MONTHLY].label,
                    color: trendTypesConfig[trendRentalType.MONTHLY].color,
                    subLabel: String(formatCount(avg_monthly_rentals)),
                },
                {
                    value: trendRentalType.TENANT,
                    label: trendTypesConfig[trendRentalType.TENANT].label,
                    color: trendTypesConfig[trendRentalType.TENANT].color,
                    subLabel: String(formatCount(avg_tenant_rentals)),
                    tooltip: 'Tenant parking subscriptions',
                },
            ],
        },
    ];
    if (avg_enterprise_rentals > 0) {
        groups.push({
            groupLabel: 'Enterprise',
            items: [
                {
                    value: trendRentalType.ENTERPRISE,
                    label: trendTypesConfig[trendRentalType.ENTERPRISE].label,
                    color: trendTypesConfig[trendRentalType.ENTERPRISE].color,
                    subLabel: String(formatCount(avg_enterprise_rentals)),
                },
            ],
        });
    }
    return groups;
}

//-------------------- Driver Reviews --------------------//

export enum ReviewsRating {
    ALL = '',
    FIVE = '5',
    FOUR = '4',
    THREE = '3',
    TWO = '2',
    ONE = '1',
}

export const reviewsRatingOptions: SelectOption[] = [
    {
        value: ReviewsRating.ALL,
        label: 'All Stars',
    },
    {
        value: ReviewsRating.FIVE,
        label: '5 Stars',
    },
    {
        value: ReviewsRating.FOUR,
        label: '4 Stars',
    },
    {
        value: ReviewsRating.THREE,
        label: '3 Stars',
    },
    {
        value: ReviewsRating.TWO,
        label: '2 Stars',
    },
    {
        value: ReviewsRating.ONE,
        label: '1 Star',
    },
];

export const MIN_SAMPLES_TO_SHOW_MODAL = 2;

// -------------------- Peak Times --------------------//

const BARS_BACKGROUND_COLOR = '#FFC9AF';
const CURRENT_COLOR = '#FF6712';

export function getPeakTimesByHourGraphData(report: PeakRentalByHour[], spot: SpotInterface) {
    const data: number[] = [];
    const labels: string[] = [];
    const currentHour = spot.timezone
        ? new Date().toLocaleTimeString('en-US', { timeZone: spot.timezone, hour: 'numeric', hour12: false })
        : new Date().getHours();
    const graphColors: string[] = [];

    for (const { hour_label, rental_count, hour_of_day } of report) {
        data.push(rental_count);
        labels.push(hour_label);
        graphColors.push(hour_of_day === Number(currentHour) ? CURRENT_COLOR : BARS_BACKGROUND_COLOR);
    }

    return {
        data,
        labels,
        graphColors,
    };
}

export function getPeakTimesByDayOfWeekGraphData(report: PeakRentalByDayOfWeek[], spot: SpotInterface) {
    const data: number[] = [];
    const labels: string[] = [];
    const graphColors: string[] = [];
    const currentDay = spot.timezone
        ? new Date().toLocaleDateString('en-US', { timeZone: spot.timezone, weekday: 'long' })
        : getDayFnToDayName[new Date().getDay()];

    for (const { day_name, rental_count, day_of_week } of report) {
        data.push(rental_count);
        labels.push(day_name);
        graphColors.push(getDayFnToDayName[day_of_week] === currentDay ? CURRENT_COLOR : BARS_BACKGROUND_COLOR);
    }
    return {
        data,
        labels,
        graphColors,
    };
}

// -------------------- Driver Destinations --------------------//
const driverDestinationCategoryGraphColors: Record<DriverDestinationCategory, string> = {
    [DriverDestinationCategory.OFFICE_WORK]: '#F58D61',
    [DriverDestinationCategory.SHOPPING]: '#7CE2FE',
    [DriverDestinationCategory.RESTAURANT_DINING]: '#59B2E5',
    [DriverDestinationCategory.ENTERTAINMENT]: '#59B8AB',
    [DriverDestinationCategory.MEDICAL]: '#B6E161',
    [DriverDestinationCategory.OTHER]: '#9D9DF2',
};

export function getDriverDestinationsGraphData(report: DriverDestination[]) {
    const data: number[] = [];
    const labels: string[] = [];
    const graphColors: string[] = [];

    for (const { destination_category, category_percentage } of report) {
        data.push(category_percentage);
        labels.push(destination_category);
        graphColors.push(driverDestinationCategoryGraphColors[destination_category]);
    }
    return { data, labels, graphColors };
}

// -------------------- Tenant Usage --------------------//

const graphColors = [
    '#FF6712',
    '#4B35B6',
    '#7CE2FE',
    '#0A9183',
    '#A5CE4D',
    '#BD0C78',
    '#AB4ABA',
    '#C7A963',
    '#FFDC00',
    '#0090FF',
];

export function getTenantUsageGraphData(selectedTenants: string[], tenantUsageMonthlyReport: TenantUsageMonthly[]) {
    // Filter the tenant usage monthly report to only include the selected tenants
    const filteredTenantUsageMonthlyReport = tenantUsageMonthlyReport.filter((t) =>
        selectedTenants.includes(t.tenant_id)
    );

    const hasUsage = filteredTenantUsageMonthlyReport.some((t) => t.usage > 0);

    // Get the unique rental months from the filtered tenant usage monthly report
    const labels = [...new Set(filteredTenantUsageMonthlyReport.map((t) => t.rental_month))].sort();

    // Create a record of tenant IDs to an array of active drivers for each rental month
    const dataByTenant: Record<string, number[]> = Object.fromEntries(
        selectedTenants.map((tenantId) => [tenantId, new Array(labels.length).fill(0)])
    );

    const tenantNamesMap: Record<string, string> = Object.fromEntries(
        selectedTenants.map((tenantId) => [
            tenantId,
            tenantUsageMonthlyReport.find((t) => t.tenant_id === tenantId)?.tenant_name ?? tenantId,
        ])
    );

    // Populate the data by tenant with the usage or active drivers for each rental month
    for (const tenantUsageMonthly of filteredTenantUsageMonthlyReport) {
        const { tenant_id, rental_month, usage, active_drivers } = tenantUsageMonthly;

        // Get month index from the labels array
        const monthIndex = labels.indexOf(rental_month);
        // Get the tenant data for the tenant ID
        const tenantData = dataByTenant[tenant_id];
        // Set the active drivers for the specific tenant and month
        tenantData[monthIndex] = hasUsage ? usage : active_drivers;
        // Update the data for the tenant ID
        dataByTenant[tenant_id] = tenantData;
    }

    return {
        graphData: {
            datasets: Object.entries(dataByTenant).map(([tenantId, data], index) => ({
                data,
                label: tenantNamesMap[tenantId],
                borderColor: graphColors[index],
                backgroundColor: graphColors[index],
            })),
            labels: labels.map((label) => getMonthYearFromString(label)),
        },
        hasUsage,
    };
}

export function hasRegionalRankingPercentile(
    regionalRanking: DriverReviewRegionalRanking | null
): regionalRanking is DriverReviewRegionalRanking {
    return regionalRanking?.regional_percentile != null && !isNaN(regionalRanking.regional_percentile);
}

// -------------------- Transient Drivers --------------------//
export const ONE_TIME_COLOR = '#F58D61';
export const RETURN_COLOR = '#7CE2FE';
export const FREQUENT_COLOR = '#0A9183';
